import { useEffect, useMemo, useState } from "react";
import { api, type MeetingListEntry, type ExpertSummary } from "../api";
import { EmptyState } from "../components/design/EmptyState";
import { Tag } from "../components/design/Tag";
import { StatusDot } from "../components/design/StatusDot";
import { Icon } from "../components/design/Icon";
import { Segmented } from "../components/design/Segmented";
import { ExpertMark } from "../components/design/ExpertMark";
import { navigate, routes } from "../router";

type ExpertMap = Record<string, ExpertSummary>;

const RETENTION_DAYS = 7;

type Filter = "all" | "scheduled" | "finalized" | "failed";
const FILTER_OPTIONS: ReadonlyArray<{ value: Filter; label: string }> = [
  { value: "all", label: "All" },
  { value: "scheduled", label: "Scheduled" },
  { value: "finalized", label: "Finalized" },
  { value: "failed", label: "Failed" },
];

/** Home route — meetings list with header + search + 4-state filter +
 *  per-row meeting cards. Replaces the previous action-grid Home.
 */
export function Meetings() {
  const [state, setState] = useState<{
    loaded: boolean;
    rows: MeetingListEntry[];
    error: string | null;
  }>({ loaded: false, rows: [], error: null });
  const [experts, setExperts] = useState<ExpertMap>({});
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>(() => {
    const params = new URLSearchParams(window.location.search);
    const v = params.get("filter");
    return v === "scheduled" || v === "finalized" || v === "failed"
      ? v
      : "all";
  });

  // Persist filter choice in the URL so reloads keep it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (filter === "all") params.delete("filter");
    else params.set("filter", filter);
    const next = params.toString();
    const url =
      window.location.pathname + (next ? `?${next}` : "") + window.location.hash;
    if (url !== window.location.pathname + window.location.search + window.location.hash) {
      window.history.replaceState({}, "", url);
    }
  }, [filter]);

  useEffect(() => {
    let cancelled = false;
    api
      .listMeetings()
      .then((page) => {
        if (cancelled) return;
        setState({ loaded: true, rows: page.meetings, error: null });
      })
      .catch((e) => {
        if (cancelled) return;
        setState({
          loaded: true,
          rows: [],
          error: e instanceof Error ? e.message : String(e),
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch the expert lineup once so each MeetingRow can resolve expert_ids
  // into monogram + color tokens for the ExpertMark stack. The strip falls
  // back to a placeholder dot when the lookup fails.
  useEffect(() => {
    let cancelled = false;
    api
      .listExperts()
      .then((list) => {
        if (cancelled) return;
        const m: ExpertMap = {};
        for (const e of list) m[e.id] = e;
        setExperts(m);
      })
      .catch(() => {
        // Best-effort: lookup is purely cosmetic.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return state.rows.filter((m) => {
      if (filter !== "all") {
        const k = classify(m);
        if (k !== filter) return false;
      }
      if (q.length > 0) {
        const expertNames = m.expert_ids
          .map((id) => experts[id])
          .filter((e): e is ExpertSummary => Boolean(e))
          .flatMap((e) => [e.short_name, e.display_name]);
        const haystack = [
          extractMeetCode(m.meet_url),
          m.meeting_id,
          m.state,
          ...expertNames,
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [state.rows, search, filter]);

  return (
    <div
      style={{
        padding: "32px 40px",
        maxWidth: 1080,
        margin: "0 auto",
      }}
    >
      <header
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 14,
          marginBottom: 20,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <h1
            className="serif"
            style={{
              margin: 0,
              fontSize: 36,
              fontWeight: 400,
              letterSpacing: "-0.01em",
            }}
          >
            Meetings
          </h1>
          <button
            type="button"
            className="btn primary"
            onClick={() => navigate(routes.meetingNew())}
          >
            <Icon name="plus" size={13} /> New meeting
          </button>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              position: "relative",
              flex: 1,
              minWidth: 240,
              maxWidth: 480,
            }}
          >
            <span
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--ink-3)",
              }}
            >
              <Icon name="search" size={14} color="var(--ink-3)" />
            </span>
            <input
              type="text"
              placeholder="Search by Meet code or id…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 10px 8px 30px",
                borderRadius: 6,
                border: "1px solid var(--rule)",
                background: "var(--card)",
                fontSize: 13,
              }}
            />
          </div>
          <Segmented<Filter>
            value={filter}
            onChange={setFilter}
            size="sm"
            options={FILTER_OPTIONS}
          />
        </div>
      </header>

      {!state.loaded && (
        <div className="dim" style={{ padding: 24 }}>
          Loading meetings…
        </div>
      )}

      {state.loaded && state.error && state.rows.length === 0 && (
        <div
          style={{
            padding: 16,
            border: "1px solid var(--err)",
            background: "color-mix(in oklab, var(--err) 8%, var(--card))",
            borderRadius: 8,
            color: "var(--ink)",
            fontSize: 14,
          }}
        >
          {state.error}
        </div>
      )}

      {state.loaded && state.rows.length === 0 && !state.error && (
        <EmptyState
          icon="spark"
          heading="Your meetings will land here."
          subline="Add the bot to a Google Meet and we'll capture, transcribe, and turn each call into a bundle."
          steps={[
            "Pick experts in the picker",
            "Send the bot to your next call",
            "Open the bundle when the meeting finalizes",
          ]}
          cta={{
            label: "Start your first meeting",
            href: routes.meetingNew(),
          }}
        />
      )}

      {state.loaded && state.rows.length > 0 && visible.length === 0 && (
        <EmptyState
          icon="search"
          heading="Nothing matches"
          subline="Clear filters and search to see all meetings."
          cta={{
            label: "Clear filters",
            onClick: () => {
              setSearch("");
              setFilter("all");
            },
          }}
        />
      )}

      {state.loaded && visible.length > 0 && (
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {visible.map((m) => (
            <MeetingRow key={m.meeting_id} m={m} experts={experts} />
          ))}
        </ul>
      )}
    </div>
  );
}

function MeetingRow({
  m,
  experts,
}: {
  m: MeetingListEntry;
  experts: ExpertMap;
}) {
  const expired = isExpired(m);
  const kind = classify(m);
  const onClick = () => {
    if (kind === "failed") {
      navigate(routes.edge("finalize_failed", m.meeting_id));
      return;
    }
    // Always route through the meeting page — its FinalizedView owns
    // the new bundle tree + per-kind previews. The legacy /jobs/<id>
    // page predates the redesign and would skip the meeting context
    // (expert tones, transcript link, frames). Resolved-job-id still
    // drives the per-file fetches inside the meeting view.
    navigate(routes.meeting(m.meeting_id));
  };
  const meetCode = extractMeetCode(m.meet_url);
  const isFailed = kind === "failed";
  const isScheduled = kind === "scheduled";
  return (
    <li
      className="recspec-meeting-row"
      onClick={onClick}
      style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr auto auto",
        gap: 16,
        alignItems: "center",
        padding: "14px 16px",
        background: isFailed
          ? "color-mix(in oklab, var(--err) 6%, var(--card))"
          : "var(--card)",
        border: `1px solid ${isFailed ? "color-mix(in oklab, var(--err) 25%, var(--rule))" : "var(--rule)"}`,
        borderRadius: 10,
        cursor: "pointer",
        opacity: isScheduled ? 0.6 : 1,
        transition: "background .12s, border-color .12s",
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.background = "var(--paper-2)")
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.background = isFailed
          ? "color-mix(in oklab, var(--err) 6%, var(--card))"
          : "var(--card)")
      }
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        {m.expert_ids.length === 0 && (
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: 5,
              background: "var(--paper-2)",
              border: "1px solid var(--rule)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--ink-3)",
              fontFamily: "var(--mono)",
              fontSize: 10,
            }}
          >
            M
          </span>
        )}
        {m.expert_ids.map((id, i) => {
          const e = experts[id];
          if (!e) return null;
          // Resolve the archetype display name from the loaded expert
          // summary for the tooltip; falls back to the bare archetype
          // id when the registry isn't loaded (api booted without
          // EXPERTS_DIR → ExpertSummary.archetypes is empty).
          const archetype =
            m.archetype_id && e.archetypes
              ? e.archetypes.find((a) => a.id === m.archetype_id)
              : undefined;
          const archetypeLabel = archetype?.display_name ?? m.archetype_id ?? "";
          return (
            <span
              key={id}
              title={archetypeLabel ? `${e.display_name} · ${archetypeLabel}` : e.display_name}
              style={{
                marginLeft: i === 0 ? 0 : -6,
                zIndex: m.expert_ids.length - i,
              }}
            >
              <ExpertMark expert={e} size={22} />
            </span>
          );
        })}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span
            className="mono"
            style={{ fontSize: 13, fontWeight: 500 }}
          >
            {meetCode}
          </span>
          {expired && (
            <Tag>
              <span className="dim">expired</span>
            </Tag>
          )}
        </div>
        <div className="dim" style={{ fontSize: 11.5, marginTop: 2 }}>
          {formatTimestamp(m.created_at)}
        </div>
      </div>
      <CountsStrip m={m} kind={kind} />
      <StatePill kind={kind} />
    </li>
  );
}

/** "X decisions · Y risks · Z frames · J questions" per the design.
 *  Hides any zero-count component so a fresh / scheduled / failed row
 *  doesn't read as "0 decisions · 0 risks · 0 frames · 0 questions" —
 *  drops to "—" if every count is zero. */
function CountsStrip({ m, kind }: { m: MeetingListEntry; kind: Filter }) {
  if (kind === "failed") {
    return (
      <span
        className="mono"
        style={{
          fontSize: 11,
          color: "var(--err)",
          textAlign: "right",
          flexShrink: 0,
        }}
      >
        bundle unavailable
      </span>
    );
  }
  const parts: string[] = [];
  if (m.decisions_count > 0) parts.push(`${m.decisions_count} decisions`);
  if (m.risks_count > 0) parts.push(`${m.risks_count} risks`);
  if (m.frames_count > 0) parts.push(`${m.frames_count} frames`);
  if (m.live_questions_count > 0)
    parts.push(`${m.live_questions_count} questions`);
  return (
    <span
      className="dim mono"
      style={{
        fontSize: 11,
        textAlign: "right",
        flexShrink: 0,
        whiteSpace: "nowrap",
      }}
    >
      {parts.length > 0 ? parts.join(" · ") : "—"}
    </span>
  );
}

function StatePill({ kind }: { kind: Filter }) {
  if (kind === "all") return null;
  const map: Record<
    Exclude<Filter, "all">,
    { label: string; color: string; pulse: boolean }
  > = {
    scheduled: { label: "Scheduled", color: "var(--ink-3)", pulse: false },
    finalized: { label: "Finalized", color: "var(--ok)", pulse: false },
    failed: { label: "Failed", color: "var(--err)", pulse: false },
  };
  const v = map[kind];
  return (
    <Tag dot>
      <StatusDot color={v.color} pulse={v.pulse} size={6} />
      {v.label}
    </Tag>
  );
}

/** Map a meeting list entry to the high-level "kind" the filter operates on.
 *  Today the wire only has 3 states (in_meeting / meeting_over / finalized);
 *  "scheduled" and "failed" are SPA-derived: scheduled = no joined_at yet,
 *  failed = finalized but no resolved_job_id. Refine when the wire grows
 *  explicit fields. */
function classify(m: MeetingListEntry): Filter {
  if (m.state === "finalized") {
    return m.resolved_job_id ? "finalized" : "failed";
  }
  if (m.state === "in_meeting" || m.state === "meeting_over") {
    return m.joined_at ? "all" : "scheduled";
  }
  return "all";
}

function isExpired(m: MeetingListEntry): boolean {
  const anchor = m.finalized_at ?? m.created_at;
  const t = Date.parse(anchor);
  if (Number.isNaN(t)) return false;
  return Date.now() - t > RETENTION_DAYS * 24 * 60 * 60 * 1000;
}

function extractMeetCode(url: string): string {
  const m = /meet\.google\.com\/([a-z0-9-]+)/i.exec(url);
  return m ? m[1] : url;
}

function formatTimestamp(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
